import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useState } from 'react';

import {
  Box,
  Container,
  Grid,
  ImageList,
  ImageListItem,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import Image from 'next/image';

import { PrimaryInput } from '@components/common/CustomInput';
import Meta from '@components/common/Head';
import Layout from '@components/common/Layout';
import PlantCard from '@components/common/PlantCard';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { colors } from '@theme/colors';

import { useQuery } from '@apollo/client';
import {
  GET_ALL_PLANT_CARE_PROPERTIES,
  GET_PLANTS_SEARCH_FILTER,
} from '@operations/plants/queries';

const styles = {
  paginationButton: {
    boxShadow: 'none',
    borderRadius: '46% 53% 59% 42% / 42% 48% 52% 58%',
    '&.MuiPaginationItem-previousNext': {
      color: colors.primary.green,
      backgroundColor: colors.primary.purple,
    },
  },
  headerSticky: {
    position: 'sticky',
    top: '58px',
    zIndex: 999,
    background: '#fff',
    paddingBottom: '8px',
  },
};

const SearchBar = ({ handleSearch, searchText, t, handleSearchTextChange }) => (
  <Box pt="30px">
    <PrimaryInput
      variant="outlined"
      placeholder={t('search_plants')}
      id="list_search"
      onClick={handleSearch}
      icon={
        <Image
          width={30}
          height={20}
          src="/assets/images/search_icon.svg"
          alt="search"
        />
      }
      onChange={handleSearchTextChange}
      value={searchText}
      aria-describedby="list_search"
      inputProps={{
        'aria-label': t('search_plants'),
      }}
    />
  </Box>
);

SearchBar.defaultProps = {
  handleSearch: () => null,
  searchText: '',
  setSearchText: () => null,
  t: (text) => text,
};

SearchBar.propTypes = {
  handleSearch: PropTypes.func,
  searchText: PropTypes.string,
  setSearchText: PropTypes.func,
  t: PropTypes.func,
};

const HeaderText = ({ t }) => (
  <Box>
    <Typography variant="h4" color="text.secondaryGreen" mb={2}>
      {t('subscription_title')}
    </Typography>
    <Typography variant="h5" fontFamily="Inter" fontWeight={500}>
      {t('subscription_subtitle')}
    </Typography>
  </Box>
);

HeaderText.defaultProps = {
  t: (text) => text,
};

HeaderText.propTypes = {
  t: PropTypes.func,
};

function LibraryList() {
  const { t } = useTranslation(['care', 'common']);
  const theme = useTheme();
  const tablet = useMediaQuery(theme.breakpoints.down('md'));
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState({});
  const [page, setPage] = useState(1);

  const handleGetToTop = () => {
    window.scrollTo(0, 0);
  };

  const { query = {} } = useRouter();
  const [searchText, setSearchText] = useState('');

  const {
    data: plantData = { plants: [], query_id: '' },
    fetchMore,
    loading,
    refetch,
  } = useQuery(GET_PLANTS_SEARCH_FILTER, {
    variables: {
      page: 0,
      pageLimit: 12,
    },
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: plantProperties = {
      lightLevels: [],
      maintenanceLevels: [],
      waterFrequencies: [],
    },
  } = useQuery(GET_ALL_PLANT_CARE_PROPERTIES, {
    fetchPolicy: 'no-cache',
  });

  const { phone = '' } = query;
  const handleSearch = () => {
    if (searchText.length) {
      refetch({
        searchString: searchText,
        userPhone: phone?.replace?.(' ', '+') || '',
        page: 0,
        pageLimit: 12,
      });
    }
  };

  const handlePagination = (event, value) => {
    handleGetToTop();
    setPage(value);
    fetchMore({
      variables: {
        page: value - 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return Object.assign({}, prev, {
          searchPlants: {
            ...fetchMoreResult.searchPlants,
            plants: [...fetchMoreResult.searchPlants.plants],
          },
        });
      },
    });
  };

  const handleChange = (panel, id) => {
    const index = expanded?.[id]?.indexOf(panel);
    if (index > -1) {
      const tempExpanded = { ...expanded };
      const temp = expanded?.[id];
      temp.splice(index, 1);
      if (temp.length > 0) {
        setExpanded({
          ...tempExpanded,
          [id]: [...temp],
        });
      } else {
        delete tempExpanded[id];
        setExpanded({
          ...tempExpanded,
        });
      }
    } else {
      setExpanded({
        ...expanded,
        [id]: [...(expanded?.[id] ? expanded?.[id] : []), panel],
      });
    }
  };

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  return (
    <Layout>
      <Meta
        title={'Get plant details on WhatsApp'}
        description={
          'Find your plants and get info about their light levels, watering, care plus expert recommendations, tips and tricks!'
        }
        imageUrl={
          'https://cf-simple-s3-origin-cloudfrontfors3-360504420918.s3.amazonaws.com/banner_v1.png'
        }
      />

      {!mobile && (
        <Container
          sx={{
            pt: { xs: '30px', sm: '60px' },
            ...styles.headerSticky,
          }}
        >
          <Grid container>
            <Grid
              item
              container
              direction="column"
              justifyContent="flex-end"
              xs={12}
              sm={6}
            >
              <HeaderText t={t} />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              container
              justifyContent={{ xs: 'center', sm: 'flex-end' }}
              alignItems="center"
            >
              <SearchBar
                t={t}
                handleSearch={handleSearch}
                searchText={searchText}
                handleSearchTextChange={handleSearchTextChange}
              />
            </Grid>
          </Grid>
        </Container>
      )}
      {mobile && (
        <>
          <Container
            sx={{
              pt: { xs: '30px', sm: '60px' },
            }}
          >
            <HeaderText t={t} />
          </Container>

          <Container sx={styles.headerSticky}>
            <SearchBar
              t={t}
              handleSearch={handleSearch}
              searchText={searchText}
              handleSearchTextChange={handleSearchTextChange}
            />
          </Container>
        </>
      )}

      <Container sx={{ pb: { xs: '30px', sm: '60px' } }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : plantData?.searchPlants?.plants?.length > 0 ? (
          <Box sx={{ py: 2 }}>
            <ImageList
              variant="quilted"
              gap={20}
              sx={{
                gridTemplateColumns: `repeat(${
                  mobile ? 1 : tablet ? 2 : 3
                }, minMax(0, 1fr)) !important`,
              }}
            >
              {plantData.searchPlants.plants?.map((item, index) => (
                <ImageListItem
                  key={index}
                  rows={expanded?.[item.id]?.length > 0 ? 2 : 1}
                >
                  <PlantCard
                    plantData={item}
                    queryId={plantData.searchPlants.query_id}
                    expand={expanded?.[item.id]}
                    handleExpand={handleChange}
                    id={item.id}
                    lightLevels={plantProperties.lightLevels}
                    maintenanceLevels={plantProperties.maintenanceLevels}
                    waterFrequencies={plantProperties.waterFrequencies}
                  />
                </ImageListItem>
              ))}
            </ImageList>
            <Box
              container
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Pagination
                count={plantData.searchPlants.total_pages}
                onChange={handlePagination}
                page={page}
                renderItem={(item) => (
                  <PaginationItem sx={styles.paginationButton} {...item} />
                )}
              />
            </Box>
          </Box>
        ) : (
          <Grid
            item
            alignItems="center"
            textAlign="center"
            xs={12}
            sm={5}
            md={4}
            mt={4}
            container
            direction="column"
          >
            <Image
              src={`/assets/images/care_big.svg`}
              width={70}
              height={56}
              layout="fixed"
              alt="hero_image_3"
            />
            <Typography variant="h5" mt={2}>
              {t('missing_plant_title')}
            </Typography>
            <Typography
              variant="subtitle1"
              fontFamily="Inter"
              color="text.tertiary"
              mt={2}
            >
              {t('missing_plant_sub_text')}
            </Typography>
          </Grid>
        )}
      </Container>
    </Layout>
  );
}

export default LibraryList;

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['care', 'common'])),
    },
  };
}
